import React from 'react';
import { Cart } from '../../types/shopify';
import './CartSidebar.scss';

interface CartSidebarProps {
  cart: Cart | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateQuantity: (lineItemId: string, quantity: number) => void;
  onRemoveItem: (lineItemId: string) => void;
}

const CartSidebar: React.FC<CartSidebarProps> = ({
  cart,
  isOpen,
  onClose,
  onUpdateQuantity,
  onRemoveItem
}) => {
  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(parseFloat(amount));
  };

  const handleCheckout = () => {
    if (cart?.checkoutUrl) {
      // Перенаправляем на shtromstudio.myshopify.com
      const url = new URL(cart.checkoutUrl);
      url.hostname = 'shtromstudio.myshopify.com';
      window.location.href = url.toString();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="cart-sidebar__overlay"
          onClick={handleOverlayClick}
        />
      )}

      {/* Sidebar */}
      <div className={`cart-sidebar ${isOpen ? 'cart-sidebar--open' : ''}`}>
        <div className="cart-sidebar__header">
          <h2 className="cart-sidebar__title">
            Cart ({cart?.totalQuantity || 0})
          </h2>
          <button 
            onClick={onClose}
            className="cart-sidebar__close"
          >
            ×
          </button>
        </div>

        <div className="cart-sidebar__content">
          {!cart || cart.lines.length === 0 ? (
            <div className="cart-sidebar__empty">
              <p>Your cart is currently empty.</p>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="cart-sidebar__items">
                {cart.lines.map((item) => (
                  <div key={item.id} className="cart-sidebar__item">
                    <div className="cart-sidebar__item-image">
                      {item.variant.image ? (
                        <img
                          src={item.variant.image.url}
                          alt={item.variant.image.altText || item.product.title}
                        />
                      ) : (
                        <div className="cart-sidebar__item-no-image">
                          No Image
                        </div>
                      )}
                    </div>

                    <div className="cart-sidebar__item-details">
                      <h3 className="cart-sidebar__item-title">
                        {item.product.title}
                      </h3>
                      
                      {item.variant.selectedOptions.length > 0 && (
                        <div className="cart-sidebar__item-options">
                          {item.variant.selectedOptions.map((option, index) => (
                            <span key={index} className="cart-sidebar__item-option">
                              {option.name}: {option.value}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="cart-sidebar__item-price">
                        {formatPrice(item.variant.price.amount, item.variant.price.currencyCode)}
                      </div>

                      <div className="cart-sidebar__item-actions">
                        <div className="cart-sidebar__item-quantity">
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="cart-sidebar__quantity-btn"
                          >
                            −
                          </button>
                          <span className="cart-sidebar__quantity-value">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                            className="cart-sidebar__quantity-btn"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={() => onRemoveItem(item.id)}
                          className="cart-sidebar__remove-btn"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="cart-sidebar__summary">
                <div className="cart-sidebar__total">
                  <span className="cart-sidebar__total-label">Total:</span>
                  <span className="cart-sidebar__total-price">
                    {formatPrice(cart.totalPrice.amount, cart.totalPrice.currencyCode)}
                  </span>
                </div>

                <button
                  onClick={handleCheckout}
                  className="cart-sidebar__checkout-btn"
                >
                  Checkout
                </button>

                <p className="cart-sidebar__checkout-note">
                  Taxes and shipping calculated at checkout
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;